import byteBuffer from 'bytebuffer';
import * as Crypto from 'expo-crypto';
import * as FileSystem from 'expo-file-system/legacy';
import mime from 'mime';
import { Platform } from 'react-native';
import uuid from 'react-native-uuid';

import { Document, LocalFile, DirectUpload, User } from '@graphql/generated';
import { getFileMetadata } from '@utils/calculateChecksum';
import { useFetchContentTypes } from '@utils/fetchContentTypes';
import { fetchWithErrorHandling } from '@utils/fetchWithErrorHandling';
import { AsyncStorage } from '@utils/storage';

import type { UploadProgressData } from 'expo-file-system/legacy';

export const getFileInfoPromises = async (
  documents: Document[]
): Promise<
  (FileSystem.FileInfo & Pick<Document, 'clientId' | 'contentType'>)[]
> => {
  return Promise.all(
    documents.map(async (document) => {
      const documentInfo = await Platform.select({
        web: async () => {
          const systemDocument = await localDocumentToFile(document);
          const { byte_size, checksum } = await getFileMetadata(systemDocument);
          return Promise.resolve({
            exists: true,
            isDirectory: false,
            modificationTime: new Date().getMilliseconds(),
            size: byte_size ?? 0,
            uri: document.file.url,
            md5: checksum,
          } as FileSystem.FileInfo);
        },
        default: async () =>
          await FileSystem.getInfoAsync(document.file.url, {
            md5: true,
            size: true,
          }),
      })();

      const { clientId, contentType } = document;

      return { ...documentInfo, clientId, contentType };
    })
  );
};

export const createDirectUploadAttributesFromFileInfo = async (
  documentInfoForImages: (FileSystem.FileInfo &
    Pick<Document, 'clientId' | 'contentType'>)[]
) => {
  const resultPromises = documentInfoForImages.map(async (item) => {
    const { size, md5: documentInfoMd5, clientId, contentType, uri } = item;
    const checksum =
      Platform.select({
        default: () =>
          documentInfoMd5 && byteBuffer.fromHex(documentInfoMd5).toBase64(),
        web: () => documentInfoMd5,
      })() || '';

    const filename = await getSafeFilename(uri, contentType || '');

    return {
      byteSize: size ?? 0,
      checksum,
      filename,
      clientId,
      contentType,
    };
  });

  return Promise.all(resultPromises);
};

export const uploadFilesAsync = async (
  documentDirectUploads: DirectUpload[],
  documents: Document[],
  fileInfoResults: any[],
  callback?: (
    documentClientId: string,
    uploadProgress: UploadProgressData,
    blobId?: string
  ) => void,
  isDeleteFileAfterSuccess?: boolean
) =>
  await Platform.select({
    default: uploadFilesAsyncNative,
    web: uploadFilesAsyncWeb,
  })(
    documentDirectUploads,
    documents,
    fileInfoResults,
    callback,
    isDeleteFileAfterSuccess
  );

// Shared with waitUntilNotUploading (useChatUploadManager.tsx) so the
// network-upload-task timeout and the caller-facing "still uploading" wait
// can never drift apart again (TA-9371: the two had independently drifted to
// 30s/300s vs 300s/600s, so this timeout fired first and killed genuine
// slow uploads before the caller's "validated" budget ever applied).
// Rate of 5s/MB assumes ~1.6 Mbps effective throughput (conservative slow-3G baseline).
export const UPLOAD_TIMEOUT_BASE_SECONDS = 300; // floor for small / size-unknown files
export const UPLOAD_TIMEOUT_PER_MB_SECONDS = 5;
export const UPLOAD_TIMEOUT_MAX_SECONDS = 600; // 10-minute hard ceiling

export const calculateUploadTimeoutMs = (sizeInMB: number): number => {
  // fileInfo.size is optional (expo-file-system), so callers may pass
  // NaN/Infinity for size-unknown files. setTimeout(NaN) resolves to an
  // immediate 0ms timeout, so clamp to 0 here to keep the 300s floor intact.
  const safeSizeInMB = Number.isFinite(sizeInMB) && sizeInMB > 0 ? sizeInMB : 0;
  return (
    Math.min(
      UPLOAD_TIMEOUT_BASE_SECONDS +
        safeSizeInMB * UPLOAD_TIMEOUT_PER_MB_SECONDS,
      UPLOAD_TIMEOUT_MAX_SECONDS
    ) * 1000
  );
};

const uploadFilesAsyncNative = async (
  documentDirectUploads: DirectUpload[],
  documents: Document[],
  fileInfoResults: any[],
  callback?: (
    documentClientId: string,
    uploadProgress: UploadProgressData,
    blobId?: string
  ) => void,
  _isDeleteFileAfterSuccess?: boolean
): Promise<{ blobId: string; clientId: string; numericBlobId: string }[]> => {
  return Promise.all(
    documentDirectUploads.map(async (directUpload, index) => {
      await Promise.resolve();
      const { signedBlobId, clientId, blobId } = directUpload;
      const headers = JSON.parse(directUpload.headers) as Record<
        string,
        string
      >;
      if (__DEV__) {
        console.log('==============upload task=============');
      }
      const document = documents[index];
      const fileInfo = fileInfoResults[index];
      const fileUri = document.file.url;
      const fileInfoCheck = await FileSystem.getInfoAsync(fileUri);
      if (!fileInfoCheck.exists) {
        const errorMsg = `File does not exist at path: ${fileUri}`;
        console.error(errorMsg);
        return;
      }

      const progressCallback = (progress: UploadProgressData) => {
        console.log({ progress });
        callback?.(document.clientId, progress, signedBlobId);
      };
      const uploadTask = FileSystem.createUploadTask(
        directUpload.url,
        document.file.url,
        {
          headers,
          httpMethod: 'PUT',
        },
        progressCallback
      );

      const sizeInMB = fileInfo.size / (1024 * 1024);
      const timeoutDurationMs = calculateUploadTimeoutMs(sizeInMB);
      if (__DEV__)
        console.log(
          `Uploading file: ${document.file.url}\nSize: ${sizeInMB.toFixed(
            2
          )}MB, Timeout: ${timeoutDurationMs / 1000}s`
        );

      try {
        const response = await Promise.race([
          uploadTask.uploadAsync(),
          new Promise<null>((_, reject) =>
            setTimeout(
              () => reject(new Error(`Upload timeout: ${clientId}`)),
              timeoutDurationMs
            )
          ),
        ]);

        if (response?.status === 200) {
          // The local file is deliberately kept. Resend re-uploads drafts from
          // it when addProjectFiles/addTaskFiles fails after this PUT, or when
          // another file in the batch fails. The old RNFS.unlink got a file://
          // URI and never deleted anything, so this matches production;
          // cleanup has to wait until the server accepts the document (TA-11049).
          return { blobId: signedBlobId, clientId, numericBlobId: blobId };
        } else {
          const errorMsg = response
            ? `Upload failed with status: ${response.status}, blobId: ${signedBlobId}, clientId: ${clientId}`
            : `Upload failed without response. blobId: ${signedBlobId}, clientId: ${clientId}`;

          console.error(errorMsg);
          throw new Error(errorMsg);
        }
      } catch (error) {
        let errorMessage = 'Unknown error occurred';

        if (error instanceof Error) {
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          errorMessage = error;
        }

        console.error(
          `Error during upload for clientId ${clientId}:`,
          errorMessage
        );
        throw new Error(errorMessage);
      }
    })
  );
};

const uploadFilesAsyncWeb = async (
  documentDirectUploads: DirectUpload[],
  documents: Document[],
  _fileInfoResults: any[],
  callback?: (
    documentClientId: string,
    uploadProgress: UploadProgressData,
    blobId?: string
  ) => void,
  _isDeleteFileAfterSuccess?: boolean
): Promise<{ blobId: string; clientId: string; numericBlobId: string }[]> => {
  return Promise.all(
    documentDirectUploads.map(async (directUpload, index) => {
      const localDocument = documents[index];
      const file = await localDocumentToFile(localDocument);

      const {
        url,
        headers: rawHeaders,
        signedBlobId,
        clientId,
        blobId,
      } = directUpload;
      const headers: Record<string, string> = Object.entries(
        JSON.parse(rawHeaders)
      ).reduce(
        (acc, [key, value]) => {
          acc[key.toLowerCase()] = value as string;
          return acc;
        },
        {} as Record<string, string>
      );

      await uploadFileWithProgressWeb({
        file,
        url,
        headers,
        clientId,
        onProgress: (progress) => {
          callback?.(clientId, progress, signedBlobId);
        },
      });

      return {
        blobId: signedBlobId,
        clientId,
        numericBlobId: blobId,
      };
    })
  );
};

const uploadFileWithProgressWeb = async ({
  file,
  url,
  headers = {},
  clientId = 'unknown',
  onProgress,
}: {
  file: File | Blob;
  url: string;
  headers?: Record<string, string>;
  clientId?: string;
  onProgress?: (progress: UploadProgressData) => void;
}): Promise<void> => {
  const supportsProgress =
    typeof XMLHttpRequest !== 'undefined' && 'upload' in new XMLHttpRequest();

  if (supportsProgress) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.open('PUT', url);

      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = {
            totalBytesSent: event.loaded,
            totalBytesExpectedToSend: event.total,
          };

          if (__DEV__) {
            console.log(
              `[Progress] clientId=${clientId} ${event.loaded}/${
                event.total
              } = ${((event.loaded / event.total) * 100).toFixed(1)}%`
            );
          }

          onProgress?.(progress);
        } else {
          if (__DEV__) {
            console.log(`[Progress] clientId=${clientId} - not computable`);
          }
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      };

      xhr.onerror = () => {
        reject(new Error('Upload failed due to a network error.'));
      };

      xhr.send(file);
    });
  } else {
    if (__DEV__) {
      console.warn(
        `[Fallback] Browser doesn't support xhr.upload.onprogress. Using fetch instead.`
      );
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Fetch fallback upload failed: ${response.statusText}`);
    }
  }
};

/**
 * Converts a `Document` type object into a `File` object
 * @param localDocument The local document
 * @returns A `File`
 */
export const localDocumentToFile = async (localDocument: Document) => {
  const {
    file: { url },
    name,
  } = localDocument;
  const res = await fetchWithErrorHandling(url);
  const blob: Blob = await res.blob();
  return new File([blob], name, { type: contentType(url) });
};

/**
 * Computes the document size of a base64 encoded document uri
 * @param documentUri A base64 encoded document uri
 * @returns The document size
 */
export const documentSize = (base64Uri: string): number => {
  // Remove data URL prefix if present
  let base64 = base64Uri;
  if (base64Uri.includes(',')) {
    base64 = base64Uri.split(',')[1];
  }

  // Determine padding count
  let padding = 0;
  if (base64.endsWith('==')) {
    padding = 2;
  } else if (base64.endsWith('=')) {
    padding = 1;
  }

  // Convert Base64 length to bytes
  return base64.length * 0.75 - padding;
};

/**
 * Retrieves a document type in mime-type format from a document uri string
 * @param documentUri The documentUri to parse
 * @returns The content type in mime-type format
 */
export const contentType = (documentUri: string) => {
  if (documentUri?.startsWith('data:')) {
    return documentUri.slice(
      documentUri.indexOf('data:') + 'data:'.length,
      documentUri.lastIndexOf(';')
    );
  }
  return '';
};

/**
 * Formats a size into a human readable display
 * @param size Size value to format
 * @returns The formatted size
 */
export const formatSize = (size: number) => {
  if (!+size) return '';

  const byteSize = 1024;
  const KB = Math.pow(byteSize, 1);
  const MB = Math.pow(byteSize, 2);

  if (size < MB) {
    return `${(size / KB).toFixed(2)} KB`;
  }
  return `${(size / MB).toFixed(2)} MB`;
};

/**
 * Reads the target document into a uri
 * @param targetFile The document to read
 * @returns An object with the document uri and optional base64 encoding
 */
export const readFileToUri = (
  targetFile: Blob
): Promise<{ uri: string; base64?: string }> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(
        new Error(
          `Failed to read the selected media because the operation failed.`
        )
      );
    };
    reader.onload = ({ target }) => {
      const uri = target?.result;

      const returnRaw = () =>
        resolve({
          uri: uri as string,
        });

      if (typeof uri === 'string') {
        resolve({
          uri,
          // The blob's result cannot be directly decoded as Base64 without
          // first removing the Data-URL declaration preceding the
          // Base64-encoded data. To retrieve only the Base64 encoded string,
          // first remove data:*/*;base64, from the result.
          // https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
          base64: uri.slice(uri.indexOf(',') + 1),
        });
      } else {
        returnRaw();
      }
    };

    reader.readAsDataURL(targetFile);
  });
};

/**
 * Opens a file in a new tab.
 *
 * **NOTE**
 * Only available in the web/browser environment.
 * @param document The document to open
 */
export const openInNewTab = async (document: Document | LocalFile) => {
  if (Platform.OS !== 'web') return;

  const file = 'file' in document ? document.file : document;
  let link = '';

  // file will always contain a url, but if we have a cdnBaseUrl,
  // we have to use that to build the path instead.
  // here change to get from cache storage
  const isLocal =
    file.url && (file.url.startsWith('file:') || file.url.startsWith('data:'));
  if (isLocal) {
    link = file.url;
  } else {
    const cachedUrl = await AsyncStorage.getItem(document.clientId);
    if (cachedUrl) {
      link = cachedUrl;
    }
  }

  if ('file' in document && link.startsWith('data:')) {
    const f = await localDocumentToFile(document);
    link = URL.createObjectURL(f);
  }

  link && window && 'open' in window && window.open(link, '_blank');
};

const base64ToArrayBuffer = (base64: string) => {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

const getDurationFromDataUrl = async (url: string) => {
  const context = new AudioContext();

  const buffer = base64ToArrayBuffer(url.split(',')[1]);

  const decoded = await context.decodeAudioData(buffer).catch(() => {
    return new AudioBuffer({ length: 0, numberOfChannels: 0, sampleRate: 0 });
  });

  return Math.round(decoded.duration);
};

/**
 * Converts a `File` type to a `LocalFile` object
 * @param file File to convert
 * @returns A LocalFile object
 */
export const fileToLocalFile = async (file: File, getAudioDuration = false) => {
  const { uri: url } = await readFileToUri(file);
  const clientId = uuid.v4().toString();
  const isAudio = file.type.startsWith('audio');
  const isVideo = file.type.startsWith('video');
  const isImage = file.type.startsWith('image');
  const duration =
    getAudioDuration && isAudio ? await getDurationFromDataUrl(url) : undefined;
  return {
    isImage: isImage || isVideo,
    contentType: file.type,
    isPreviewable: isImage || isVideo,
    thumbnail: '',
    name: file.name,
    url,
    size: file.size,
    clientId,
    id: clientId,
    cdnBaseUrl: '',
    path: '',
    isAudio,
    duration,
    __typename: 'LocalFile',
  } as LocalFile;
};

/**
 * Safely extracts a filename from URI and optionally contentType.
 * Falls back to UUID + appropriate extension when needed.
 */
export const getSafeFilename = async (
  uri?: string,
  contentType?: string
): Promise<string> => {
  if (!uri) {
    return `${uuid.v4().toString()}.${await getExtensionByContentType(
      contentType
    )}`;
  }

  try {
    // Skip blob: and data: URIs
    if (uri.startsWith('blob:') || uri.startsWith('data:')) {
      return `${uuid.v4().toString()}.${await getExtensionByContentType(
        contentType
      )}`;
    }

    // Extract last segment of the URI
    const raw = uri.split('/').pop() ?? '';
    const clean = raw.split('?')[0].split('#')[0];

    // If clean name looks okay (has extension and not too long), use it
    if (clean.length <= 100 && clean.includes('.')) {
      return clean;
    }

    return `${uuid.v4().toString()}.${await getExtensionByContentType(
      contentType
    )}`;
  } catch {
    return `${uuid.v4().toString()}.${await getExtensionByContentType(
      contentType
    )}`;
  }
};

let cachedContentTypes: Record<string, string[]> | null = null;
export const getExtensionByContentType = async (
  contentType: string | undefined
): Promise<string> => {
  const DEFAULT_EXTENSION = '.unknown';

  if (!contentType) {
    return DEFAULT_EXTENSION;
  }

  try {
    if (!cachedContentTypes) {
      const { fetchAllowedContentTypes } = useFetchContentTypes();
      let contentTypesString = await AsyncStorage.getItem('allContentTypes');

      if (!contentTypesString) {
        await fetchAllowedContentTypes();
        contentTypesString = await AsyncStorage.getItem('allContentTypes');
      }

      if (!contentTypesString) {
        return DEFAULT_EXTENSION;
      }

      cachedContentTypes = JSON.parse(contentTypesString);
    }

    const extensions = cachedContentTypes?.[contentType];

    return extensions && extensions!.length > 0
      ? extensions[0]
      : DEFAULT_EXTENSION;
  } catch {
    return DEFAULT_EXTENSION;
  }
};

export const mapLocalFilesToDocuments = (
  files: LocalFile[],
  owner?: User
): Document[] => {
  const nowISOString = new Date().toISOString();
  return files.map((item) => ({
    __typename: 'Document',
    id: item.clientId,
    uuid: item.clientId,
    name: item.name,
    clientId: item.clientId,
    messageClientId: item.clientId,
    messageCreateYn: false,
    isImage: !!item.isImage,
    isAudio: !!item.isAudio,
    isVideo: !!item.isVideo,
    contentType: item.contentType ?? '',
    isPreviewable: item.isPreviewable,
    thumbnail: item.thumbnail,
    size: item.size ?? 0,
    duration: item.duration ?? null,
    createdAt: nowISOString,
    updatedAt: nowISOString,
    failedAt: '',
    expired: false,
    owner: owner as unknown as User,
    file: {
      ...item,
      __typename: 'File',
      isPreviewable: item.isPreviewable,
      thumbnail: item.thumbnail,
    },
    projectDocuments: [],
    taskDocuments: [],
    messageDocuments: [],
    folder: null,
    blobId: item.blobId,
  }));
};

/**
 * Builds a draft chat Document attachment from a LocalFile.
 *
 * Delegates to mapLocalFilesToDocuments so every field required by the
 * DocumentDetail / MessageDetail fragments (isVideo, taskDocuments,
 * projectDocuments, ...) lives in one place — hand-written attachment
 * objects drifting from the fragments caused Apollo missing-field
 * warnings on cache writes (TA-10232).
 */
export const mapLocalFileToDraftDocument = (
  item: LocalFile,
  messageClientId: string,
  // Required: DocumentDetail selects owner, so a missing owner would
  // reintroduce the missing-field warnings this helper exists to prevent.
  owner: User
): Document => ({
  ...mapLocalFilesToDocuments([item], owner)[0],
  messageClientId,
});

/**
 * Builds the draft Document written to listDraftProjectDocuments /
 * listDraftTaskDocuments (LocalDocumentDetail) for a picked or captured file.
 * Every field in that selection must be present: a missing blobId logged
 * Apollo's "Missing field 'blobId'" on camera Done (TA-11049).
 */
export const mapLocalFileToDraftUploadDocument = (
  item: LocalFile,
  parentId: string | null | undefined,
  owner: User,
  isConnected: boolean | null | undefined
): Document => {
  const {
    name,
    clientId,
    contentType,
    isImage = false,
    isAudio = false,
    isVideo = false,
    duration = null,
    url,
  } = item;
  const documentId = `${parentId}-${clientId}`;
  const now = new Date().toISOString();

  return {
    __typename: 'Document',
    id: documentId,
    uuid: clientId,
    blobId: item.blobId ?? null,
    owner,
    name: name || clientId,
    clientId,
    contentType,
    isImage,
    createdAt: now,
    updatedAt: now,
    failedAt: isConnected ? null : now,
    size: item.size,
    file: {
      ...item,
      __typename: 'File',
      id: documentId,
      cdnBaseUrl: '',
      url,
    },
    isAudio,
    duration,
    isVideo,
  };
};

export const mapDocumentsToLocalFiles = (
  documents: Document[]
): LocalFile[] => {
  return documents.map((doc) => {
    const file = doc.file;

    return {
      id: doc.clientId,
      clientId: doc.clientId,
      name: doc.name,
      url: file.url ?? file.path ?? '',
      cdnBaseUrl: file.cdnBaseUrl ?? '',
      path: file.path ?? '',
      contentType: doc.contentType ?? '',
      isImage: doc.isImage,
      isVideo: doc.isVideo,
      isAudio: doc.isAudio,
      size: doc.size ?? 0,
      duration: doc.duration ?? null,
      isPreviewable: file.isPreviewable ?? false,
      thumbnail: file.thumbnail ?? '',
      blobId: doc.blobId,
    };
  });
};

export const computeFileHash = async (fileUri: string): Promise<string> => {
  if (!fileUri) {
    console.warn('computeFileHash file url can not be empty!');
    return '';
  }
  if (Platform.OS === 'web') {
    return computeWebHash(fileUri);
  } else {
    return computeNativeHash(fileUri);
  }
};

export const computeNativeHash = async (fileUri: string): Promise<string> => {
  const mimeType = mime.getType(fileUri) ?? '';
  const isImage = mimeType.startsWith('image/');

  if (isImage) {
    try {
      const base64Content = await FileSystem.readAsStringAsync(fileUri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      return await Crypto.digestStringAsync(
        Crypto.CryptoDigestAlgorithm.SHA256,
        base64Content,
        { encoding: Crypto.CryptoEncoding.HEX }
      );
    } catch (e) {
      console.warn('[generateFingerprintFromUri] Image hash fallback:', e);
    }
  }

  // AdrianKao review, PR #5914: unlike the image path above, this had no
  // try/catch -- an unreadable content:// URI (e.g. a rejected video whose
  // selectFiles.ts cache copy failed) throws here, rejecting the whole
  // computeFileHash call. removeUploadingFile awaits that call before its
  // cleanup step, so a rejection could leave the upload-manager entry/cached
  // file uncleared. Fail closed to a fallback hash instead.
  //
  // TA-10885 (main): separately, a file that is no longer on disk reads
  // successfully but reports exists: false -- neither size nor
  // modificationTime, so the fallback fingerprint below would collapse to a
  // per-mime-type constant ('0--image/jpeg'). Every vanished image would then
  // share one hashUploadMap key and be handed back an unrelated file's
  // blobId -- a blob the server may well have dropped, surfacing to the user
  // as "The file has been deleted and cannot be sent". No file, no
  // fingerprint: callers already treat '' as "this file cannot be deduped".
  // {size: true} dropped -- InfoOptions only defines `md5`, and the native
  // side always includes `size`/`modificationTime` when exists: true
  // regardless of options; the option was a no-op that failed typechecking.
  let fileSize = 0;
  let fileTime: string | number = '';
  // Only set on a genuine read error (not the exists:false case, which
  // returns '' outright) -- disambiguates two different files that both
  // fail to read from colliding on the same "0--mimeType" fallback. Kept
  // out of the normal fallback below so a readable file's fingerprint
  // depends only on its content characteristics (size/mtime/mime), matching
  // the TA-10885 dedup contract -- not its ephemeral local cache path.
  let readErrorSuffix = '';
  try {
    const fileInfo = await FileSystem.getInfoAsync(fileUri);
    if (!fileInfo.exists) {
      console.warn('[computeNativeHash] file no longer exists:', fileUri);
      return '';
    }
    fileSize = fileInfo.size;
    fileTime = fileInfo.modificationTime;
  } catch (e) {
    console.warn('[generateFingerprintFromUri] getInfoAsync fallback:', e);
    readErrorSuffix = `-${fileUri}`;
  }

  const fallbackParts = [String(fileSize), String(fileTime), mimeType];
  const fallbackStr = fallbackParts.join('-') + readErrorSuffix;
  if (__DEV__) {
    console.log(
      '[generateFingerprintFromUri] fallback fingerprint string:',
      fallbackStr
    );
  }

  return await Crypto.digestStringAsync(
    Crypto.CryptoDigestAlgorithm.SHA256,
    fallbackStr,
    { encoding: Crypto.CryptoEncoding.HEX }
  );
};

export const computeWebHash = async (fileUrl: string): Promise<string> => {
  const response = await fetchWithErrorHandling(fileUrl);
  const buffer = await response.arrayBuffer();
  const digestBuffer = await crypto.subtle.digest('SHA-256', buffer);

  const hashArray = Array.from(new Uint8Array(digestBuffer));
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hashHex;
};
